import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Tags, Plus, Trash2, Loader2, GripVertical, X, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * SMB Lead Categories — admin-controlled list that powers the
 * Add/Edit Lead form Select + Filter dropdown across the SMB module.
 *
 * Defaults (seed) can be deactivated but not deleted. Operator-created
 * rows can be deleted (hard delete — they're slug-immutable). Existing
 * leads tagged with a deactivated category keep their tag — only the
 * dropdown stops offering it for new leads.
 */
export const SmbCategoriesCard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [editing, setEditing] = useState(null); // {id, label, sort_order}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/smb/categories');
      if (r.data?.success) setRows(r.data.data || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return null;

  const addNew = async () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error('Enter a category label');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/smb/categories', { label });
      if (r.data?.success) {
        toast.success(`Added "${r.data.data.label}"`);
        setNewLabel('');
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Add failed');
    } finally { setBusy(false); }
  };

  const toggleActive = async (row) => {
    setBusy(true);
    try {
      await api.put(`/smb/categories/${row.id}`, { is_active: !row.is_active });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const label = (editing.label || '').trim();
    if (!label) {
      toast.error('Label cannot be empty');
      return;
    }
    setBusy(true);
    try {
      await api.put(`/smb/categories/${editing.id}`, {
        label,
        sort_order: parseInt(editing.sort_order) || 0,
      });
      toast.success('Saved');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (row.is_default) {
      toast.error('Default categories cannot be deleted — deactivate instead');
      return;
    }
    if (!window.confirm(`Delete "${row.label}"? Existing leads tagged with this category will keep their tag.`)) return;
    setBusy(true);
    try {
      await api.delete(`/smb/categories/${row.id}`);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="smb-categories-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tags className="h-5 w-5 text-emerald-500" />SMB Lead Categories
        </CardTitle>
        <CardDescription>
          Configure the business categories shown in the Add/Edit Lead form. Defaults can be deactivated
          (hidden from new leads) but not deleted — existing tagged leads always keep their category.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Add new */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs">New category label</Label>
            <Input
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              placeholder="e.g. Architect"
              onKeyDown={e => { if (e.key === 'Enter') addNew(); }}
              data-testid="cat-new-label"
            />
          </div>
          <Button onClick={addNew} disabled={busy || !newLabel.trim()} data-testid="cat-add">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" />Add</>}
          </Button>
        </div>

        {/* List */}
        <div className="rounded-lg border border-border divide-y divide-border max-h-[400px] overflow-y-auto">
          {rows.length === 0 && (
            <p className="text-xs text-muted-foreground italic text-center py-6">No categories yet.</p>
          )}
          {rows.map(row => {
            const isEditing = editing?.id === row.id;
            return (
              <div key={row.id}
                   className={`flex items-center gap-2 px-3 py-2 ${!row.is_active ? 'opacity-60' : ''}`}
                   data-testid={`cat-row-${row.slug}`}>
                <GripVertical className="h-3.5 w-3.5 text-muted-foreground/40 flex-shrink-0" />
                {isEditing ? (
                  <>
                    <Input
                      value={editing.label}
                      onChange={e => setEditing({ ...editing, label: e.target.value })}
                      className="h-8 text-sm flex-1"
                      data-testid={`cat-edit-label-${row.slug}`}
                    />
                    <Input
                      type="number"
                      value={editing.sort_order}
                      onChange={e => setEditing({ ...editing, sort_order: e.target.value })}
                      className="h-8 w-20 text-sm"
                      title="Sort order"
                      data-testid={`cat-edit-order-${row.slug}`}
                    />
                    <Button size="sm" variant="ghost" onClick={saveEdit} disabled={busy}
                            className="h-8 w-8 p-0" data-testid={`cat-save-${row.slug}`}>
                      <Check className="h-3.5 w-3.5 text-emerald-500" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}
                            className="h-8 w-8 p-0">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium truncate">{row.label}</span>
                      <code className="text-[10px] text-muted-foreground bg-muted px-1 rounded">
                        {row.slug}
                      </code>
                      {row.is_default && (
                        <Badge variant="outline" className="text-[9px] border-emerald-500/40 text-emerald-400">
                          default
                        </Badge>
                      )}
                      {!row.is_active && (
                        <Badge variant="outline" className="text-[9px] border-red-500/40 text-red-400">
                          inactive
                        </Badge>
                      )}
                    </div>
                    <span className="text-[10px] text-muted-foreground w-10 text-right">
                      #{row.sort_order}
                    </span>
                    <Switch
                      checked={row.is_active}
                      onCheckedChange={() => toggleActive(row)}
                      disabled={busy}
                      data-testid={`cat-toggle-${row.slug}`}
                    />
                    <Button size="sm" variant="ghost"
                            onClick={() => setEditing({ id: row.id, label: row.label, sort_order: row.sort_order, slug: row.slug })}
                            className="h-8 px-2 text-[11px]"
                            data-testid={`cat-edit-${row.slug}`}>
                      Edit
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(row)}
                            disabled={busy || row.is_default}
                            className="h-8 w-8 p-0"
                            title={row.is_default ? 'Defaults cannot be deleted — deactivate' : 'Delete'}
                            data-testid={`cat-delete-${row.slug}`}>
                      <Trash2 className={`h-3.5 w-3.5 ${row.is_default ? 'opacity-30' : 'text-red-500'}`} />
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-muted-foreground italic">
          Tip: lower sort_order values appear first. Use multiples of 10 to leave room to insert new
          categories between existing rows without renumbering everything.
        </p>
      </CardContent>
    </Card>
  );
};

export default SmbCategoriesCard;
